import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import { getPhotoLightboxRoot } from "../utils/photoLightboxRoot";
import { closePhotoLightbox, usePhotoLightbox } from "../utils/photoLightboxStore";

export { usePhotoLightbox } from "../utils/photoLightboxStore";

export function ClickablePhoto({ src, alt, className = "", onView, fill = false }) {
  if (!src) return null;

  return (
    <button
      type="button"
      className={`rs-re-photo-clickable${fill ? " rs-re-photo-clickable--fill" : ""} ${className}`.trim()}
      onClick={() => onView(src, alt)}
      aria-label={`View full size: ${alt}`}
    >
      <img src={src} alt={alt} className="rs-re-photo-clickable__img" />
      <span className="rs-re-photo-clickable__zoom" aria-hidden="true">
        <Icon icon="tabler:zoom-in" width={22} height={22} />
      </span>
    </button>
  );
}

/** Mount once near the app root (e.g. getstarted.jsx). */
export default function PhotoLightboxHost() {
  const { lightbox } = usePhotoLightbox();
  const [ready, setReady] = useState(false);
  const isOpen = !!lightbox;

  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closePhotoLightbox();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  if (!ready) return null;

  return createPortal(
    <div
      className={`rs-re-photo-lightbox${isOpen ? " is-open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Full size photo"
      aria-hidden={!isOpen}
    >
      <button
        type="button"
        className="rs-re-photo-lightbox__backdrop"
        onClick={closePhotoLightbox}
        aria-label="Close full size photo"
        tabIndex={isOpen ? 0 : -1}
      />
      <div className="rs-re-photo-lightbox__panel">
        <button
          type="button"
          className="rs-re-photo-lightbox__close"
          onClick={closePhotoLightbox}
          aria-label="Close full size photo"
          tabIndex={isOpen ? 0 : -1}
        >
          <Icon icon="tabler:x" width={24} height={24} />
        </button>
        {lightbox ? (
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="rs-re-photo-lightbox__img"
          />
        ) : null}
      </div>
    </div>,
    getPhotoLightboxRoot()
  );
}
